"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  findNominalizations,
  type NominalizationKind,
  type NominalizationMatch,
} from "@/lib/tool-kit/writing-analysis/nominalizations";

/** English strings. A translated content file overrides these through `ui`. */
export const NOMINALIZATION_FINDER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "The implementation of the new attendance policy was followed by a reduction in absenteeism, but an analysis of the data suggests that this improvement was not uniform across schools. In schools where staff made a decision to delay enforcement, there was an increase in late arrivals during the first term. A comparison of the two groups provides an indication that the effectiveness of the policy depended on the consistency of its application. Further investigation of teacher discretion is of considerable importance before any recommendation can be made.",
  enterPrompt:
    "Paste or type some text to see its nominalizations — the nouns with a verb or adjective hiding inside them.",
  results: "Results",
  words: "Words",
  found: "Nominalizations",
  perHundred: "Per 100 words",
  phrases: "Verb-weakening phrases",
  nouns: "Known nominalizations",
  possible: "Possible nominalizations",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with nominalizations highlighted",
  legendPhrase: "Verb-weakening phrase",
  legendNoun: "Nominalization",
  legendPossible: "Possible nominalization",
  hoverHint:
    "Hover over a highlight to see the verb or adjective to try; the table below lists every one.",
  titleSuggestion: "Try: {suggestion}",
  titlePossible:
    "Possible nominalization — is there a verb or adjective inside it?",
  noneFound:
    "No nominalizations found. Your sentences keep their actions in verbs.",
  denseHeading: "Sentences to revise first",
  denseHint:
    "Each of these carries three or more nominalizations — the likeliest places for an action to be buried in nouns.",
  nominalizationCount: "one: nominalization|other: nominalizations",
  tableHeading: "Nominalizations and what to try instead",
  colNominalization: "Nominalization",
  colTimes: "Times",
  colTry: "Try instead",
  colType: "Found as",
  kindPhrase: "In a phrase",
  kindNoun: "Noun",
  kindPossible: "Possible",
  lookInside: "the verb or adjective inside it",
  judgement:
    "A prompt, not a rule: many nominalizations name a concept precisely. Revise the ones that hide who did what.",
} as const;

type Strings = typeof NOMINALIZATION_FINDER_STRINGS;

const TONES: Record<NominalizationKind, HighlightTone> = {
  phrase: "rose",
  noun: "amber",
  possible: "sky",
};

function titleFor(match: NominalizationMatch, strings: Strings): string {
  if (match.kind === "possible") {
    return strings.titlePossible;
  }
  return t(strings.titleSuggestion, { suggestion: match.suggestion });
}

function kindLabel(kind: NominalizationKind, strings: Strings): string {
  if (kind === "phrase") {
    return strings.kindPhrase;
  }
  return kind === "noun" ? strings.kindNoun : strings.kindPossible;
}

function Legend({ strings }: { strings: Strings }) {
  const items: [NominalizationKind, string][] = [
    ["phrase", strings.legendPhrase],
    ["noun", strings.legendNoun],
    ["possible", strings.legendPossible],
  ];
  return (
    <p className="flex flex-wrap gap-3 text-sm">
      {items.map(([kind, label]) => (
        <span className={HIGHLIGHT_TONES[TONES[kind]]} key={kind}>
          {label}
        </span>
      ))}
    </p>
  );
}

export default function NominalizationFinderWidget() {
  const strings = useToolUi(NOMINALIZATION_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(() => findNominalizations(deferred), [deferred]);

  const highlights: TextMatch[] = report.matches.map((match) => ({
    start: match.start,
    end: match.end,
    category: match.kind,
    title: titleFor(match, strings),
  }));
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="nf-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.found}
                value={fmt(report.matches.length)}
              />
              <StatCard
                label={strings.perHundred}
                value={fmt(report.density, { maximumFractionDigits: 1 })}
              />
              <StatCard label={strings.words} value={fmt(report.words)} />
              <StatCard label={strings.phrases} value={fmt(report.phrases)} />
              <StatCard label={strings.nouns} value={fmt(report.nouns)} />
              <StatCard label={strings.possible} value={fmt(report.possible)} />
            </StatGrid>
          </div>
          <p className={HINT}>{strings.judgement}</p>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <Legend strings={strings} />
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {report.matches.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : null}

          {report.denseSentences.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.denseHeading}</h3>
              <p className={HINT}>{strings.denseHint}</p>
              <ul className="space-y-2">
                {report.denseSentences.map((sentence) => (
                  <li className={CARD} key={sentence.start}>
                    <p className="text-foreground text-sm leading-relaxed">
                      {sentence.text}
                    </p>
                    <p className="mt-2 font-medium text-muted-foreground text-xs">
                      {fmt(sentence.count)}
                      {tPlural(
                        strings.nominalizationCount,
                        locale,
                        sentence.count
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {report.byNoun.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className={TABLE}>
                  <thead>
                    <tr>
                      <th className={TH} scope="col">
                        {strings.colNominalization}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colTimes}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colTry}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colType}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byNoun.map((row) => (
                      <tr key={row.noun}>
                        <td className={`${TD} font-medium`}>{row.noun}</td>
                        <td className={TD}>{fmt(row.count)}</td>
                        <td className={TD}>
                          {row.suggestion || (
                            <span className="text-muted-foreground">
                              {strings.lookInside}
                            </span>
                          )}
                        </td>
                        <td className={`${TD} text-muted-foreground`}>
                          {kindLabel(row.kind, strings)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
